import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Plus, Search, Eye, Truck, RefreshCw } from 'lucide-react';
import axios from 'axios';
import useCustomFields from '../hooks/useCustomFields';
import CustomFieldCell from '../components/custom-fields/CustomFieldCell';
import DataTable from '../components/common/DataTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROCESS', label: 'In Process' },
  { value: 'PARTIALLY_DELIVERED', label: 'Partially Delivered' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const getStatusColor = (status) => {
  switch (status) {
    case 'DRAFT': return 'bg-gray-100 text-gray-800';
    case 'OPEN': return 'bg-orange-100 text-orange-800';
    case 'IN_PROCESS': return 'bg-yellow-100 text-yellow-800';
    case 'PARTIALLY_DELIVERED': return 'bg-orange-100 text-orange-800';
    case 'DELIVERED': return 'bg-green-100 text-green-800';
    case 'COMPLETED': return 'bg-emerald-100 text-emerald-800';
    case 'CANCELLED': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const SalesOrderList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [salesOrders, setSalesOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    customer_id: '',
    sales_org: ''
  });
  const [customers, setCustomers] = useState([]);
  const [salesOrgs, setSalesOrgs] = useState([]);

  // Frappe-style: pull custom fields flagged for list view
  const { fields: listCustomFields } = useCustomFields('sales_order', { listViewOnly: true });

  useEffect(() => {
    loadMasterData();
    loadSalesOrders();
  // eslint-disable-next-line
  }, []);

  const loadMasterData = async () => {
    try {
      const [custRes, orgRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/sales-organizations`).catch(() => ({ data: [] }))
      ]);
      setCustomers(custRes.data || []);
      setSalesOrgs(orgRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  const loadSalesOrders = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.customer_id) params.append('customer_id', filters.customer_id);
      if (filters.sales_org) params.append('sales_org', filters.sales_org);

      const response = await axios.get(`${BACKEND_URL}/api/otc/sales-orders?${params.toString()}`);
      setSalesOrders(response.data || []);
    } catch (error) {
      console.error('Error loading sales orders:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load sales orders: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    loadSalesOrders();
  };

  const handleClearFilters = () => {
    setFilters({ status: '', customer_id: '', sales_org: '' });
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    return customer?.customer_name || customerId;
  };

  const columns = [
    {
      key: 'sales_order_number',
      header: 'Order #',
      filterable: true,
      accessor: (o) => o.sales_order_number,
      render: (o) => <span className="font-mono font-bold text-orange-600">{o.sales_order_number}</span>,
    },
    {
      key: 'customer',
      header: 'Customer',
      filterable: true,
      accessor: (o) => `${getCustomerName(o.sold_to_party)} ${o.sold_to_party}`,
      render: (o) => (
        <div>
          <p className="font-medium">{getCustomerName(o.sold_to_party)}</p>
          <p className="text-xs text-gray-500">{o.sold_to_party}</p>
        </div>
      ),
    },
    {
      key: 'sales_organization',
      header: 'Sales Org',
      filterable: true,
      accessor: (o) => o.sales_organization,
    },
    {
      key: 'document_date',
      header: 'Date',
      accessor: (o) => o.document_date?.split('T')[0],
    },
    {
      key: 'net_value',
      header: 'Net Value',
      align: 'right',
      render: (o) => (
        <span className="font-mono">{o.currency} {o.net_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
      ),
    },
    {
      key: 'gross_value',
      header: 'Gross Value',
      align: 'right',
      render: (o) => (
        <span className="font-mono font-bold">{o.currency} {o.gross_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      filterable: true,
      filterType: 'select',
      filterOptions: STATUS_OPTIONS,
      accessor: (o) => o.status,
      render: (o) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(o.status)}`}>
          {o.status?.replace('_', ' ')}
        </span>
      ),
    },
    ...listCustomFields.map((f) => ({
      key: `cf_${f.fieldname}`,
      header: f.label,
      render: (o) => (
        <span data-testid={`so-cf-${f.fieldname}-${o.sales_order_number}`}>
          <CustomFieldCell field={f} value={o.custom_data?.[f.fieldname]} />
        </span>
      ),
    })),
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (o) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="outline" onClick={() => navigate(`/otc/sales-orders/${o.sales_order_number}`)} title="View Order" data-testid={`view-order-${o.sales_order_number}`}>
            <Eye className="w-4 h-4" />
          </Button>
          {['OPEN', 'IN_PROCESS', 'PARTIALLY_DELIVERED'].includes(o.status) && (
            <Button size="sm" variant="outline" onClick={() => navigate(`/otc/deliveries/create?so=${o.sales_order_number}`)} title="Create Delivery">
              <Truck className="w-4 h-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sales Orders</h1>
            <p className="text-sm text-gray-600 mt-1">Manage customer orders and track fulfillment</p>
          </div>
          {canWrite('sales_order') && (
          <Button onClick={() => navigate('/otc/sales-orders/create')} data-testid="create-sales-order-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Sales Order
          </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
              <Select value={filters.status || '__ALL__'} onValueChange={(v) => setFilters({...filters, status: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Customer</label>
              <Select value={filters.customer_id || '__ALL__'} onValueChange={(v) => setFilters({...filters, customer_id: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Customers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>
                      {c.customer_id} - {c.customer_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Sales Org</label>
              <Select value={filters.sales_org || '__ALL__'} onValueChange={(v) => setFilters({...filters, sales_org: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Sales Orgs" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Sales Orgs</SelectItem>
                  {salesOrgs.map(org => (
                    <SelectItem key={org.sales_org_id} value={org.sales_org_id}>
                      {org.sales_org_id} - {org.sales_org_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSearch} data-testid="search-btn">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        </Card>

        {/* Sales Orders Table */}
        <Card className="p-6">
          <DataTable
            testid="sales-orders-table"
            columns={columns}
            data={salesOrders}
            loading={isLoading}
            rowKey={(o) => o.sales_order_number}
            emptyText="No sales orders found."
            emptyHint="Create a new sales order to get started."
            rowProps={(o) => ({ 'data-testid': `order-${o.sales_order_number}` })}
          />
        </Card>
      </div>
    </Layout>
  );
};

export default SalesOrderList;
